import React from "react";
import SharedLayout from "./SharedLayout";
import performanceStressImage from "../images/performance_stress.png";

const PerformanceCoachingView = ({ user, onLoginClick }) => {
  return (
    <SharedLayout user={user} onLoginClick={onLoginClick}>
      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "2rem" }}>
        <h1
          style={{
            textAlign: "center",
            marginBottom: "2rem",
            color: "#2c3e50",
            fontSize: "2rem",
          }}
        >
          Performance Coaching
        </h1>

        <div
          style={{
            backgroundColor: "white",
            padding: "2rem",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            marginBottom: "2rem",
          }}
        >
          <p
            style={{
              fontSize: "1.2rem",
              fontWeight: "500",
              color: "#2c3e50",
              marginBottom: "2rem",
              textAlign: "center",
            }}
          >
            <span className="sigmund-text">Sigmund</span> is a valuable
            Performance Coaching tool.
          </p>

          <div
            style={{
              fontSize: "1.1rem",
              lineHeight: "1.8",
              color: "#2c3e50",
            }}
          >
            <p style={{ marginBottom: "2rem" }}>
              The relationship between a human's emotional state and peak
              performance is a well-studied topic, often explained through
              frameworks like the Yerkes-Dodson Law and modern psychology.
              Emotional states influence focus, energy, and decision-making, all
              of which are critical for performing at your best. Generally, peak
              performance—whether in sports, work, or creative pursuits—occurs
              in a "sweet spot" of emotional arousal and affects both mental and
              physical performance.
            </p>

            <div
              style={{
                margin: "3rem 0",
                textAlign: "center",
              }}
            >
              <img
                src={performanceStressImage}
                alt="Performance vs Stress Graph"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              />
            </div>

            <p style={{ marginBottom: "2rem" }}>
              Too little emotion (like boredom or apathy) can leave you
              disengaged and sluggish, while too much (like overwhelming anxiety
              or anger) can cloud judgment and disrupt execution. The
              Yerkes-Dodson Law, for instance, suggests an inverted U-shaped
              curve: performance improves with emotional arousal up to a point,
              after which it declines if the intensity gets too high.
            </p>

            <p style={{ marginBottom: "2rem" }}>
              From a mental perspective, imagine a calm chess player versus one
              trembling with nerves—moderate excitement sharpens focus, but
              panic scatters it. Positive emotions, like confidence or
              enthusiasm, tend to broaden your attention and boost resilience.
            </p>

            <h2
              style={{
                color: "#2c3e50",
                fontSize: "1.5rem",
                marginTop: "3rem",
                marginBottom: "1.5rem",
              }}
            >
              Physical Performance and Emotional State
            </h2>

            <p style={{ marginBottom: "2rem" }}>
              From a physical perspective, the Yerkes-Dodson Law ties directly
              into physical performance by showing how arousal—essentially your
              body's emotional and physiological activation—affects how well you
              move, react, and endure. It's the same inverted U-shaped curve:
              too little arousal, and you're flat; too much, and you're a mess.
              But with physical tasks, it's less about mental clarity and more
              about energy, coordination, and muscle efficiency.
            </p>

            <div style={{ marginBottom: "2rem" }}>
              <h3
                style={{
                  color: "#3498db",
                  fontSize: "1.2rem",
                  marginBottom: "1rem",
                }}
              >
                Low Arousal State
              </h3>
              <p>
                At low arousal, your body is not primed. Think of a runner
                slouching to the starting line, half-asleep—heart rate's low,
                adrenaline's MIA, muscles feel heavy. Reaction times lag, and
                effort is weak. Studies on athletes (like sprinters or
                weightlifters) show that without enough activation, power output
                and speed suffer. You're basically stuck in neutral.
              </p>
            </div>

            <div style={{ marginBottom: "2rem" }}>
              <h3
                style={{
                  color: "#3498db",
                  fontSize: "1.2rem",
                  marginBottom: "1rem",
                }}
              >
                Optimal Arousal State
              </h3>
              <p>
                Crank arousal to moderate levels, and you hit the gold zone.
                This is where athletes often describe feeling "ready" or "dialed
                in." Physiologically, your sympathetic nervous system kicks
                on—heart rate rises, blood pumps to muscles, adrenaline sharpens
                reflexes. Research, like from the Journal of Sport & Exercise
                Psychology, shows this sweet spot optimizes motor skills and
                endurance. A basketball player nailing free throws or a swimmer
                pacing perfectly—they're aroused enough to be explosive and
                focused, but not so much that they're jittery or tense. For
                simple physical tasks (say, deadlifting a max weight), this peak
                might lean toward higher arousal, since raw energy trumps
                finesse.
              </p>
            </div>

            <div style={{ marginBottom: "2rem" }}>
              <h3
                style={{
                  color: "#3498db",
                  fontSize: "1.2rem",
                  marginBottom: "1rem",
                }}
              >
                High Arousal State
              </h3>
              <p>
                Push past that into high arousal, and things unravel.
                Overactivation—think panic-level stress or uncontrolled
                adrenaline—throws off coordination and wastes energy. Your
                muscles might tense too much (like gripping a tennis racket so
                hard you choke the swing), or you might overthink mechanics and
                stumble. Studies on "choking" in sports—like golfers missing
                easy putts under pressure—link this to excessive cortisol and a
                flooded nervous system. Fatigue sets in faster too; you're
                burning fuel inefficiently. Ever seen a sprinter false-start
                from nerves? That's the downside of the curve.
              </p>
            </div>

            <p style={{ marginBottom: "2rem" }}>
              Task type tweaks the equation. Simple, strength-based stuff (e.g.,
              powerlifting) tolerates higher arousal—anger or hype can even
              help. Complex moves (e.g., a gymnast's routine) demand lower
              arousal for precision and timing. Coaches use this all the time:
              psyching up a football player with loud music versus calming a
              diver with breathing drills.
            </p>

            <p style={{ marginBottom: "2rem" }}>
              So, for physical performance, the key is regulation: elite
              performers often excel because they've mastered channeling
              emotions rather than being ruled by them.
            </p>

            <p style={{ marginBottom: "2rem" }}>
              For both physical and mental performance, people who can dial in
              their emotions to match the task outperform those who can't. And
              high-resolution "dialing in" involves a well-informed starting
              point. Sigmund understands.
            </p>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "white",
            padding: "2rem",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            marginTop: "2rem",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: "1.1rem",
              lineHeight: "1.8",
              color: "#2c3e50",
              fontWeight: "500",
            }}
          >
            Performance Coaches ordering{" "}
            <span className="sigmund-text">Sigmund</span> on a client's behalf
            are entitled to a referral fee of 35% (where allowed).
          </p>
        </div>
      </div>
    </SharedLayout>
  );
};

export default PerformanceCoachingView;
